/**
 * Shared relative time formatting + live-updating hook.
 *
 * formatRelativeTime  — "just now", "3m ago", "2h ago", "4d ago", then date
 * formatRelativeDate  — "Today", "Yesterday", "Tomorrow", "In 3 days", "4 days ago", then date
 * useNow              — hook that ticks every 30s so timestamps auto-update
 */

import { useState, useEffect } from 'react';

/* ── Compact relative time ("3m ago") ──────────────────── */

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ── Date-relative ("Today", "Yesterday", "In 3 days") ── */

export function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  if (diff < 0) {
    const absDiff = Math.abs(diff);
    if (absDiff < 7) return `In ${absDiff} days`;
    if (absDiff < 30) return `In ${Math.floor(absDiff / 7)} weeks`;
    return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  }
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

/* ── Live-updating "now" hook ────────────────────────── */

const TICK_INTERVAL = 30_000; // 30 seconds

/**
 * Returns a tick counter that increments every 30 seconds.
 * Components using formatRelativeTime will re-render automatically,
 * keeping displayed times fresh.
 *
 * Usage:
 *   useNow();                       // just call it — no need to use return value
 *   formatRelativeTime(dateStr);    // will re-compute on each tick
 */
export function useNow(): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), TICK_INTERVAL);
    return () => clearInterval(id);
  }, []);

  return tick;
}
